import {FACTIONS} from "../../../constants/factions";
import {TERRAIN_TOKENS} from "../../../constants/tokens";
import {CELECTIALS_MELEE_DAMAGE_DICE_COUNT} from "../../../configs/factions/celectials";
import {MELEE_COUNTED_DAMAGE_DICE_COUNT} from "../../../configs/damage";
import {getDamageDiceFaceOutcomes} from "../../dice/getDamageDiceFaceOutcomes";
import {getMeleeCountedDiceRolls} from "../../melee/getMeleeDiceRolls";
import {BOT_EXPECTED_DICE_PENALTY} from "../botConfig";
import {isPreW42C1Player, isPreW42C3Player, isPreW42R1Player, isPreW42U1Player} from "../abTestGates";
import {checkIfW36GoldPlusPlayer} from "../evaluation/goldPlus";

// Средний штраф урона отряда, который считает лучшие кости из нескольких: все
// равновероятные броски честных костей
const calcExpectedCountedDiceSum = (diceCount, countedDiceCount) => {
  const faces = getDamageDiceFaceOutcomes();
  let sum = 0;
  let total = 0;
  const walk = (rolls) => {
    if (rolls.length === diceCount) {
      sum += [...rolls].sort((a, b) => b - a).slice(0, countedDiceCount).reduce((acc, roll) => acc + roll, 0);
      total += 1;
      return;
    }
    faces.forEach((face) => walk([...rolls, face]));
  };
  walk([]);
  return sum / total;
};

// Лучшие 2 кости из 4 в среднем дают −0.93 к урону против −2.33 у остальных фракций
export const W42_CELECTIALS_EXPECTED_DICE_PENALTY = -calcExpectedCountedDiceSum(
  CELECTIALS_MELEE_DAMAGE_DICE_COUNT, MELEE_COUNTED_DAMAGE_DICE_COUNT
);

/**
 * Ожидаемый урон отряда в ближнем бою в расчетах бота botPlayerId. Кости —
 * свойство отряда: соблазненный селестиал бросает те же 4
 */
export const getBotExpectedMeleeDamage = (unit, botPlayerId) => {
  const penalty = unit.factionId === FACTIONS.CELECTIALS && !isPreW42C1Player(botPlayerId)
    ? W42_CELECTIALS_EXPECTED_DICE_PENALTY
    : BOT_EXPECTED_DICE_PENALTY;
  return Math.max(0, unit.size - penalty);
};

// Кавалерия в найме: у летуна дальность 4 работает в любую сторону (+0.09 индекса побед, отчет 41)
export const checkIfW42CelectialsCavalryPlayer = (player) => (
  player?.factionId === FACTIONS.CELECTIALS && !isPreW42U1Player(player.id)
);

// Штурмовая игра — архетип раша, доктрина и атака нейтралов как у агрессивных фракций.
// Только с золота: на бронзе она стоила 0.36 индекса побед, с золота дает до 0.27 (отчет 41)
export const checkIfW42CelectialsStormPlayer = (player, gameType) => (
  player?.factionId === FACTIONS.CELECTIALS && !isPreW42R1Player(player.id)
  && checkIfW36GoldPlusPlayer(player, gameType)
);

const flightHexesCache = new WeakMap();

// Жетоны для расстояний полета: над горами летают, над морем — нет
const getFlightDistanceHexes = (hexes) => {
  const cached = flightHexesCache.get(hexes);
  if (cached) {
    return cached;
  }
  const flightHexes = {};
  Object.keys(hexes).forEach((hexKey) => {
    const token = hexes[hexKey];
    if (token === TERRAIN_TOKENS.SEA) {
      flightHexes[hexKey] = TERRAIN_TOKENS.MOUNTAIN;
    } else if (token !== TERRAIN_TOKENS.MOUNTAIN) {
      flightHexes[hexKey] = token;
    }
  });
  flightHexesCache.set(hexes, flightHexes);
  return flightHexes;
};

/**
 * Жетоны для расстояний в перемещении игрока: у селестиалов дорога к цели
 * идет напрямую над горами, у остальных — вокруг них
 */
export const getBotMovementDistanceHexes = (player, hexes) => (
  player?.factionId === FACTIONS.CELECTIALS && !isPreW42C3Player(player.id)
    ? getFlightDistanceHexes(hexes)
    : hexes
);

const countSelected = (mask) => {
  let count = 0;
  for (let bit = mask; bit > 0; bit >>= 1) {
    count += bit & 1;
  }
  return count;
};

// Наборы костей от меньшего к большему: при равном выигрыше берем меньший
const DICE_SUBSETS_BY_SIZE = Array.from({length: 2 ** CELECTIALS_MELEE_DAMAGE_DICE_COUNT - 1}, (_, index) => index + 1)
  .sort((a, b) => countSelected(a) - countSelected(b));

const toDiceEntry = ([diceRoll1, diceRoll2, diceRoll3, diceRoll4]) => ({diceRoll1, diceRoll2, diceRoll3, diceRoll4});

// Выигрыш меньше этого — погрешность деления, а не выигрыш
const MIN_EXPECTED_GAIN = 1e-9;

/**
 * Какие из 4 костей селестиала перебросить за дополнительное снабжение. Урон
 * считает лучшие две, поэтому решает не грань кости, а средний прирост лучшей
 * пары: перебираются все наборы костей, при равном выигрыше — меньший набор.
 * Лучшую грань не перебрасываем: больше она не станет.
 *
 * @param {Object} params
 * @param {Array<number>} params.rolls - 4 кости
 * @param {number} [params.minRoll=1] - пол броска d6 при перебросе
 * @param {number} [params.rollBonus=0] - прибавка к значению d6 при перебросе
 * @param {number} [params.facePenalty=0] - штраф к грани при перебросе
 * @param {function(number, number): number} [params.getValue] - польза лучшей
 *   пары костей; без нее — сумма пары
 * @returns {Array<boolean>} какие кости перебросить
 */
export const selectCelectialsRerollDice = ({rolls, minRoll = 1, rollBonus = 0, facePenalty = 0, getValue}) => {
  const outcomes = getDamageDiceFaceOutcomes(minRoll, rollBonus, facePenalty);
  const bestFace = Math.max(...outcomes);
  // Польза зависит только от пары, а наборов бросков — сотни
  const valueByPair = new Map();
  const getPairValue = (faces) => {
    const [first, second] = getMeleeCountedDiceRolls(toDiceEntry(faces));
    const pairKey = `${first},${second}`;
    if (!valueByPair.has(pairKey)) {
      valueByPair.set(pairKey, getValue ? getValue(first, second) : first + second);
    }
    return valueByPair.get(pairKey);
  };

  const currentValue = getPairValue(rolls);
  let bestMask = 0;
  let bestGain = 0;
  DICE_SUBSETS_BY_SIZE.forEach((mask) => {
    const selectedIndexes = rolls.map((_, index) => index).filter((index) => (mask >> index) & 1);
    if (selectedIndexes.some((index) => rolls[index] >= bestFace)) {
      return;
    }
    let valueSum = 0;
    let total = 0;
    const walk = (position, faces) => {
      if (position === selectedIndexes.length) {
        valueSum += getPairValue(faces);
        total += 1;
        return;
      }
      outcomes.forEach((face) => {
        const nextFaces = [...faces];
        nextFaces[selectedIndexes[position]] = face;
        walk(position + 1, nextFaces);
      });
    };
    walk(0, rolls);
    const gain = valueSum / total - currentValue;
    if (gain > bestGain + MIN_EXPECTED_GAIN) {
      bestMask = mask;
      bestGain = gain;
    }
  });

  return rolls.map((_, index) => Boolean((bestMask >> index) & 1));
};
